import { useState, useEffect } from "react";
import { useParams } from "wouter";
import { useAssets } from "@/contexts/AssetContext.jsx";
import AssetLayout from "./asset-layout.jsx";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.jsx";
import { X } from "lucide-react";
import { get12MonthEventsForAsset } from "@/api/downtime-api.js";
import { errorUtils } from "@/lib/utils.js";
import { Button } from "@/components/ui/button.jsx";
import { calculateDowntimeHoursInRange } from "@/lib/downtime-data-utils.js";
import MonthlyAvailabilityTable from "@/components/monthly-availability-table.jsx";
import TopDowntimeReasonsTable from "@/components/top-downtime-reasons-table.jsx";
import DowntimeEventsTable from "@/components/downtime-events-table.jsx";

/**
 * Asset Downtime Analysis page component
 * Displays downtime analysis information for a specific asset
 * @returns {JSX.Element} Asset Downtime Analysis page
 */
export default function AssetDowntimeAnalysis() {
  const { id } = useParams();
  const { assets: compressors } = useAssets();
  const assetNumber = decodeURIComponent(id);
  const compressor = compressors?.find(
    (c) =>
      c.assetNumber === assetNumber
      || c.assetNumber === id
      || c.id === assetNumber
      || c.id === id
  );
  const [downtimeData, setDowntimeData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);

  useEffect(() => {
    const fetchDowntimeData = async () => {
      if (!compressor || !compressor.id) {
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const data = await get12MonthEventsForAsset(compressor.id);

        setDowntimeData(data);
      } catch (err) {
        errorUtils.handleApiError("fetching downtime data", err);
        setError("Failed to load downtime data");
        setDowntimeData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDowntimeData();
  }, [compressor]);

  // Derive top downtime reasons data from data loaded into state.
  const findTopDowntimeReasons = () => {
    if (
      !downtimeData?.DowntimeEvents
      || downtimeData.DowntimeEvents.length === 0
    ) {
      return [];
    }

    // Get the selected month's date range if a month is selected
    let monthStart = null;
    let monthEnd = null;
    if (selectedMonth) {
      const selectedMonthData = monthlyAvailability.find(
        (m) => m.month === selectedMonth
      );
      if (selectedMonthData) {
        // Reconstruct the month's date range
        const now = new Date();
        const monthIndex = monthlyAvailability.findIndex(
          (m) => m.month === selectedMonth
        );
        if (monthIndex !== -1) {
          const monthDate = new Date(
            now.getFullYear(),
            now.getMonth() - monthIndex,
            1
          );
          const year = monthDate.getFullYear();
          const month = monthDate.getMonth();
          monthStart = new Date(year, month, 1);
          monthEnd = new Date(year, month + 1, 0, 23, 59, 59, 999);
        }
      }
    }

    // Top Downtimes are determined by RelatedAlarm.
    // Group all downtime events by RelatedAlarm first and then get summary data for the top 10.
    const reasonMap = {};

    downtimeData.DowntimeEvents.forEach((event) => {
      // Filter by selected month if one is selected
      if (monthStart && monthEnd) {
        const eventDowntimeHours = calculateDowntimeHoursInRange(
          event,
          monthStart,
          monthEnd
        );
        if (eventDowntimeHours === 0) {
          return; // Skip events that don't fall within the selected month
        }
      }

      const reason = event.RelatedAlarm || "Unknown";
      const type = event.IsMechanical;

      const reasonKey = `${reason}-${type}`;

      if (!reasonMap[reasonKey]) {
        reasonMap[reasonKey] = {
          reason,
          type,
          eventCount: 0,
          totalDowntimeHours: 0,
        };
      }

      // Calculate downtime hours for this event (within selected month if filtered)
      const eventDowntimeHours =
        monthStart && monthEnd
          ? calculateDowntimeHoursInRange(event, monthStart, monthEnd)
          : event.DowntimeHours || 0;

      reasonMap[reasonKey].totalDowntimeHours += eventDowntimeHours;
      reasonMap[reasonKey].eventCount++;
    });

    const reasonsArray = Object.values(reasonMap).map((item) => {
      return {
        reason: item.reason,
        type: item.type,
        eventCount: item.eventCount,
        totalDowntimeHours: item.totalDowntimeHours,
      };
    });

    return reasonsArray
      .sort(
        (a, b) =>
          b.eventCount - a.eventCount
          || b.totalDowntimeHours - a.totalDowntimeHours
      )
      .slice(0, 10);
  };

  // Calculate monthly availability data
  const calculateMonthlyAvailability = () => {
    if (
      !downtimeData?.DowntimeEvents
      || downtimeData.DowntimeEvents.length === 0
    ) {
      return [];
    }

    const monthlyData = [];
    const now = new Date();

    // Generate 12 months going backwards from current month
    for (let i = 0; i < 12; i++) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const year = monthDate.getFullYear();
      const month = monthDate.getMonth();

      // Get start and end of the month
      const monthStart = new Date(year, month, 1);
      // For the current month, use now as the end date; otherwise use end of month
      const isCurrentMonth = i === 0;
      const monthEnd = isCurrentMonth
        ? now
        : new Date(year, month + 1, 0, 23, 59, 59, 999);

      // Calculate total hours in the month
      // For the current month, use elapsed hours instead of total hours
      let totalHoursInMonth;
      if (isCurrentMonth) {
        // Calculate elapsed hours from start of month to now
        const elapsedMilliseconds = now.getTime() - monthStart.getTime();
        totalHoursInMonth = elapsedMilliseconds / (1000 * 60 * 60);
      } else {
        // For past months, use total hours in the month
        const daysInMonth = monthEnd.getDate();
        totalHoursInMonth = daysInMonth * 24;
      }

      // Calculate downtime hours and mechanical downtime hours for this month
      let totalDowntimeHours = 0;
      let mechanicalDowntimeHours = 0;
      const eventsInMonth = new Set(); // Use Set to avoid counting same event twice

      downtimeData.DowntimeEvents.forEach((event, index) => {
        const eventDowntimeHours = calculateDowntimeHoursInRange(
          event,
          monthStart,
          monthEnd
        );

        if (eventDowntimeHours > 0) {
          totalDowntimeHours += eventDowntimeHours;

          // Check if this is a mechanical downtime event
          if (event.IsMechanical === 1) {
            mechanicalDowntimeHours += eventDowntimeHours;
          }

          // Track unique events that have downtime in this month
          // Use UniqueId if available, otherwise use index as fallback
          const eventKey = event.UniqueId || `event-${index}`;
          eventsInMonth.add(eventKey);
        }
      });

      const eventCount = eventsInMonth.size;
      const hasData = eventCount > 0;

      // Calculate runtime percentage (total hours - downtime hours) / total hours * 100
      const runtimeHours = totalHoursInMonth - totalDowntimeHours;
      const runtimePercentage = hasData
        ? totalHoursInMonth > 0
          ? (runtimeHours / totalHoursInMonth) * 100
          : 100
        : null;

      // Calculate mechanical availability percentage
      const mechanicalAvailabilityHours =
        totalHoursInMonth - mechanicalDowntimeHours;
      const mechanicalAvailabilityPercentage = hasData
        ? totalHoursInMonth > 0
          ? (mechanicalAvailabilityHours / totalHoursInMonth) * 100
          : 100
        : null;

      // Format month name (e.g., "Dec 25") to match app date format
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const monthName = `${monthNames[month]} ${year.toString().slice(-2)}`;

      monthlyData.push({
        month: monthName,
        runtimePercentage,
        mechanicalAvailabilityPercentage,
        downtimeHours: hasData ? totalDowntimeHours : null,
        mechanicalDowntimeHours,
        eventCount: hasData ? eventCount : null,
        totalHoursInMonth,
        hasData,
      });
    }

    return monthlyData;
  };

  const monthlyAvailability = calculateMonthlyAvailability();

  // Filter events by selected month (depends on monthlyAvailability)
  const filterEventsByMonth = () => {
    const allEvents = downtimeData?.DowntimeEvents || [];

    if (!selectedMonth || monthlyAvailability.length === 0) {
      return allEvents;
    }

    // Get the selected month's date range
    const selectedMonthData = monthlyAvailability.find(
      (m) => m.month === selectedMonth
    );
    if (!selectedMonthData) {
      return allEvents;
    }

    // Reconstruct the month's date range
    const now = new Date();
    const monthIndex = monthlyAvailability.findIndex(
      (m) => m.month === selectedMonth
    );
    if (monthIndex === -1) {
      return allEvents;
    }

    const monthDate = new Date(
      now.getFullYear(),
      now.getMonth() - monthIndex,
      1
    );
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const monthStart = new Date(year, month, 1);
    const monthEnd = new Date(year, month + 1, 0, 23, 59, 59, 999);

    // Filter events that have downtime hours within the selected month
    return allEvents.filter((event) => {
      const eventDowntimeHours = calculateDowntimeHoursInRange(
        event,
        monthStart,
        monthEnd
      );
      return eventDowntimeHours > 0;
    });
  };

  // Calculate top downtime reasons (depends on monthlyAvailability for month filtering)
  const topReasons = findTopDowntimeReasons();

  // Filter events by selected month for Downtime Events table
  const events = filterEventsByMonth();

  // Calculate 12-month summary totals
  const calculateSummaryTotals = () => {
    if (monthlyAvailability.length === 0) {
      return null;
    }

    // Only include months that have data
    const monthsWithData = monthlyAvailability.filter((month) => month.hasData);

    if (monthsWithData.length === 0) {
      return null;
    }

    let totalHours = 0;
    let totalDowntimeHours = 0;
    let totalMechanicalDowntimeHours = 0;
    const allEvents = new Set();

    monthsWithData.forEach((month) => {
      totalHours += month.totalHoursInMonth;
      totalDowntimeHours += month.downtimeHours;
      totalMechanicalDowntimeHours += month.mechanicalDowntimeHours;
    });

    // Count unique events across all months
    if (downtimeData?.DowntimeEvents) {
      downtimeData.DowntimeEvents.forEach((event, index) => {
        const eventKey = event.UniqueId || `event-${index}`;
        allEvents.add(eventKey);
      });
    }

    const totalRuntimeHours = totalHours - totalDowntimeHours;
    const totalMechanicalAvailabilityHours =
      totalHours - totalMechanicalDowntimeHours;

    const runtimePercentage =
      totalHours > 0 ? (totalRuntimeHours / totalHours) * 100 : 100;
    const mechanicalAvailabilityPercentage =
      totalHours > 0
        ? (totalMechanicalAvailabilityHours / totalHours) * 100
        : 100;

    return {
      runtimePercentage,
      mechanicalAvailabilityPercentage,
      totalDowntimeHours,
      totalEventCount: allEvents.size,
    };
  };

  const summaryTotals = calculateSummaryTotals();

  if (!compressor) {
    return null;
  }

  return (
    <AssetLayout>
      <div className='space-y-6 sm:space-y-8'>
        <Card className='bg-card border border-border'>
          <CardHeader>
            <div className='flex items-start justify-between gap-4'>
              <div>
                <CardTitle>Downtime Analysis</CardTitle>
                <p className='text-sm text-muted-foreground mt-1'>
                  12-month availability trends and downtime patterns
                </p>
              </div>
              {selectedMonth && (
                <Button
                  variant='outline'
                  size='sm'
                  className='flex items-center gap-2 border-border text-foreground hover:bg-muted'
                  onClick={() => setSelectedMonth(null)}>
                  <span>Clear Filter: {selectedMonth}</span>
                  <X className='h-4 w-4' />
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className='space-y-4'>
              {/* Top Row */}
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                {/* Monthly Availability card */}
                <MonthlyAvailabilityTable
                  monthlyAvailability={monthlyAvailability}
                  summaryTotals={summaryTotals}
                  selectedMonth={selectedMonth}
                  onMonthSelect={setSelectedMonth}
                  isLoading={isLoading}
                  error={error}
                />

                {/* Downtime Reasons card */}
                <TopDowntimeReasonsTable
                  topReasons={topReasons}
                  selectedMonth={selectedMonth}
                  isLoading={isLoading}
                  error={error}
                />
              </div>

              {/* Bottom Row */}
              <DowntimeEventsTable
                events={events}
                selectedMonth={selectedMonth}
                isLoading={isLoading}
                error={error}
                isEnbaseAndEnalysis={compressor?.optimizationEnabled === true}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </AssetLayout>
  );
}
